import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, EyeOff, MessageSquare } from 'lucide-react';
import type { ConversationSummary, TeamPostDetail, TeamPostPerson } from '@up2d8/contracts';
import {
  AppSubBar,
  Avatar,
  Button,
  Card,
  Callout,
  EmptyState,
  LoadingState,
  useToast,
} from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { PaperChecklist } from '../communication/paper-checklist';

/**
 * Eén verplicht bericht in het team (Stap 17, TEAM-2, frames 2a en 2b).
 *
 * Twee frames in één component, en dat is met opzet: het verschil is één veld uit het antwoord
 * (`people === null`). Twee componenten zouden betekenen dat iemand de namenkant kan aanpassen
 * zonder de naamloze kant te zien, en dan loopt de uitleg uit elkaar met de werkelijkheid.
 *
 * Het uitlegvlak volgt `namesWithheldReason`: er zijn drie redenen en drie zinnen, want "dit
 * bericht vroeg geen bevestiging" is een ander gesprek dan "je bedrijf zette namen uit". Eén
 * algemene zin zou de ploegbaas bij de verkeerde persoon doen aankloppen.
 */
export function TeamPostPanel({ postId, teamId }: { postId: string; teamId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, has } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['my-team-post', postId, teamId],
    // Niets uit de cache: een cijfer over mensen van gisteren is erger dan geen cijfer.
    staleTime: 0,
    queryFn: async (): Promise<TeamPostDetail> => {
      const { data, error } = await api.GET('/api/v1/me/team/posts/{postId}', {
        params: { path: { postId }, query: teamId ? { teamId } : {} } as never,
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as TeamPostDetail;
    },
  });

  const remind = useMutation({
    mutationFn: async (body: { personId?: string; all?: boolean }) => {
      const { data, error } = await api.POST('/api/v1/me/team/posts/{postId}/reminders', {
        params: { path: { postId } },
        body: body as never,
      });
      if (error || !data) throw error ?? new Error('Herinneren mislukt');
      return data as { recipients: number; remindedAt: string };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['my-team-post', postId] });
      await queryClient.invalidateQueries({ queryKey: ['my-team-open-counts'] });
      toast.show({
        title: t('team.reminderSent', { count: result.recipients }),
        tone: 'success',
      });
    },
    /*
      Een 409 is hier geen fout maar een antwoord.

      De server weigert een tweede herinnering binnen 24 uur en zegt wanneer de vorige verstuurd
      is. Dat tijdstip hoort op de knop te komen, dus wordt het antwoord opnieuw opgehaald en de
      melding een gewone mededeling - geen rode fout voor iets wat de ploegbaas goed deed.
    */
    onError: async (error: unknown) => {
      const probleem = error as { code?: string; detail?: string };
      if (probleem?.code === 'REMINDER_TOO_SOON') {
        await queryClient.invalidateQueries({ queryKey: ['my-team-post', postId] });
        toast.show({ title: t('team.reminderTooSoon'), tone: 'info' });
        return;
      }
      toast.show({
        title: t('team.reminderFailed'),
        description: probleem?.detail,
        tone: 'danger',
      });
    },
  });

  if (detail.isPending) return <LoadingState rows={5} label={t('common.loading')} />;
  if (!detail.data) {
    return (
      <Card>
        <EmptyState title={t('team.postGone')} />
      </Card>
    );
  }

  const post = detail.data;
  const openMensen = post.people?.notAcknowledged ?? [];
  const nogOpen = post.expected - post.acknowledged;

  return (
    <div className="flex flex-col gap-element" data-testid="team-post-detail">
      <AppSubBar title={t('team.postTitle')} onBack={() => void navigate({ to: '/app/team' })} />

      <Card>
        <h1 className="font-heading text-page font-semibold text-text-primary">{post.title}</h1>

        {post.countsSuppressed ? (
          // Klein team zonder leesbevestiging: één zin, geen balk en geen cijfers (ADR-0041).
          <p className="mt-element text-content text-text-secondary" data-testid="team-post-small">
            {t('team.sentToTeam')}
          </p>
        ) : (
          <dl className="mt-element grid grid-cols-3 gap-element" data-testid="team-post-counts">
            {[
              { label: t('team.recipients'), value: post.expected },
              { label: t('team.seen'), value: post.seen },
              { label: t('team.acknowledged'), value: post.acknowledged },
            ].map((cel) => (
              <div key={cel.label}>
                <dd className="font-heading text-page font-bold text-text-primary">{cel.value}</dd>
                <dt className="text-small text-text-muted">{cel.label}</dt>
              </div>
            ))}
          </dl>
        )}
      </Card>

      {/*
        "Herinner iedereen" werkt ook zonder namen (spec §1): wie geen namen mag zien, mag zijn
        ploeg wél aansporen. Daarom staat deze knop buiten de namenkant.
      */}
      {!post.countsSuppressed && nogOpen > 0 ? (
        <Button
          data-testid="team-remind-all"
          loading={remind.isPending && remind.variables?.all === true}
          disabled={remind.isPending}
          onClick={() => remind.mutate({ all: true })}
        >
          {t('team.remindAll', { count: nogOpen })}
        </Button>
      ) : null}

      {/* De afvinklijst op papier (Stap 42a): een omlijnde knop, alleen als namen mogen. */}
      {post.people !== null && has('reach.paper.record') ? (
        <div className="flex flex-wrap gap-2" data-testid="team-paper">
          <PaperChecklist
            postId={postId}
            teamId={teamId}
            people={openMensen.map((mens) => ({
              personId: mens.personId,
              displayName: mens.displayName,
            }))}
          />
        </div>
      ) : null}

      {post.people === null ? (
        <Callout tone="info" data-testid="team-names-withheld">
          <span className="flex items-start gap-2">
            <EyeOff aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
            <span>
              {t(`team.namesWithheld.${post.namesWithheldReason ?? 'no_right'}`)}
              {post.countsSuppressed ? null : (
                <span className="mt-1 block text-small text-text-muted">
                  {t('team.confirmedOf', { done: post.acknowledged, total: post.expected })}
                  {nogOpen > 0 ? ` · ${t('team.stillOpen', { count: nogOpen })}` : ''}
                </span>
              )}
            </span>
          </span>
        </Callout>
      ) : (
        <>
          <section className="flex flex-col gap-element">
            <h2 className="font-heading text-title-sm font-semibold text-text-primary">
              {t('team.notAcknowledgedTitle', { count: openMensen.length })}
            </h2>
            {openMensen.length === 0 ? (
              <Card compact>
                <p className="flex items-center gap-2 text-content text-text-secondary">
                  <CheckCircle2 aria-hidden className="h-5 w-5 text-success" />
                  {t('team.everyoneConfirmed')}
                </p>
              </Card>
            ) : (
              openMensen.map((mens) => (
                <Card key={mens.personId} compact>
                  <div className="flex items-center gap-element">
                    <Avatar name={mens.displayName} size="md" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-heading text-content font-medium text-text-primary">
                        {mens.displayName}
                      </p>
                      <p className="text-small text-[var(--orange-tint-text-aa)]">
                        {mens.seenAt ? t('team.seenNotAcknowledged') : t('team.notSeen')}
                      </p>
                    </div>
                  </div>
                  <div className="mt-element flex flex-wrap gap-2">
                    <StartConversationButton personId={mens.personId} name={mens.displayName} />
                    {mens.remindedAt ? (
                      // Uitgeschakeld met het tijdstip erin: dat is de hele reden dat de server
                      // `remindedAt` teruggeeft bij een 409.
                      <Button variant="secondary" disabled data-testid="team-reminded-at">
                        {t('team.remindedAt', { time: timeOf(mens.remindedAt) })}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        data-testid="team-remind-one"
                        // Alleen de knop van wie herinnerd wordt draait, niet die van iedereen.
                        loading={remind.isPending && remind.variables?.personId === mens.personId}
                        disabled={remind.isPending}
                        onClick={() => remind.mutate({ personId: mens.personId })}
                      >
                        {t('team.remind')}
                      </Button>
                    )}
                  </div>
                </Card>
              ))
            )}
          </section>

          <section className="flex flex-col gap-element">
            <h2 className="font-heading text-title-sm font-semibold text-text-primary">
              {t('team.acknowledgedTitle', { count: post.people.acknowledged.length })}
            </h2>
            <Card compact>
              <ul className="flex flex-col divide-y divide-border-light">
                {post.people.acknowledged.map((mens) => (
                  <AcknowledgedRow key={mens.personId} person={mens} />
                ))}
                {post.people.acknowledged.length === 0 ? (
                  <li className="py-2 text-small text-text-muted">{t('team.nobodyYet')}</li>
                ) : null}
              </ul>
            </Card>
          </section>
        </>
      )}

      {/* De privacyregel onder het scherm (spec §3): hij hoort bij dit gegeven en niet in een menu. */}
      <p className="text-small text-text-muted" data-testid="team-privacy-note">
        {t('team.privacyNote')}
      </p>
    </div>
  );
}

function AcknowledgedRow({ person }: { person: TeamPostPerson }) {
  const { t } = useTranslation();
  return (
    <li className="flex min-h-touch items-center gap-element py-2">
      <Avatar name={person.displayName} size="md" />
      <span className="min-w-0 flex-1 truncate text-content text-text-primary">
        {person.displayName}
      </span>
      <span className="flex items-center gap-1 text-small text-[var(--success-tint-text-aa)]">
        <CheckCircle2 aria-hidden className="h-4 w-4" />
        {t('team.acknowledgedAt', { time: timeOf(person.acknowledgedAt) })}
      </span>
    </li>
  );
}

/** Alleen het uur en de minuut: "Herinnerd om 08:12" (spec §1). */
function timeOf(moment: string | null): string {
  if (!moment) return '—';
  return new Date(moment).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
}

/**
 * "Stuur bericht" bij een teamlid (Stap 34c).
 *
 * Opent het bestaande gesprek of begint een nieuw; de server beslist dat, en die aanroep is
 * idempotent. Zonder het recht op gesprekken staat de knop er niet.
 */
export function StartConversationButton({ personId, name }: { personId: string; name: string }) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const start = useMutation({
    mutationFn: async (): Promise<string> => {
      const { data, error } = await api.POST('/api/v1/conversations', { body: { personId } });
      if (error || !data) throw error ?? new Error('Starten mislukt');
      return (data as ConversationSummary).id;
    },
    onSuccess: (conversationId) => {
      // Een nieuw gesprek staat nog niet in de gecachte lijst; zonder verversen vindt het
      // gesprekscherm het niet terug.
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void navigate({ to: '/app/gesprekken/$conversationId', params: { conversationId } });
    },
    onError: () => toast.show({ title: t('messaging.startFailed'), tone: 'danger' }),
  });

  if (!has('messaging.conversation.use')) return null;

  return (
    <Button
      variant="secondary"
      data-testid="team-start-conversation"
      aria-label={t('messaging.startWith', { name })}
      loading={start.isPending}
      onClick={() => start.mutate()}
    >
      <MessageSquare aria-hidden className="mr-1 h-4 w-4" />
      {t('team.sendMessage')}
    </Button>
  );
}
